import { useState, useEffect } from 'react';
import { RefreshCw, Plus, Trash2, Edit2, Save, X, Image, WandSparkles, Sofa, Hammer, Palette, FileText, ChevronRight, Cpu } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
    fetchEnhanceToggles,
    createEnhanceToggle,
    updateEnhanceToggle,
    deleteEnhanceToggle,
    fetchModeOptions,
    createModeOption,
    updateModeOption,
    deleteModeOption,
    fetchInteriorStyles,
    createInteriorStyle,
    updateInteriorStyle,
    deleteInteriorStyle,
    fetchPrompts,
    fetchPromptDetail,
    updatePrompt,
    createPrompt,
    fetchGeneratorConfig,
    updateActiveGenerators,
    EnhanceToggle,
    ModeOption,
    InteriorStyle,
    PromptTemplate,
    MODE_LABELS,
    GENERATOR_LABELS,
    type GeneratorRegistryEntry,
    type ActiveGeneratorsConfig,
} from '../../../lib/prompt-admin-api';

type ModeTabType = 'base-prompts' | 'enhance' | 'staging' | 'renovation' | 'styles';

export function AdvancedPromptsTab() {
    const [activeMode, setActiveMode] = useState<ModeTabType>('base-prompts');

    const modeTabs = [
        { id: 'base-prompts' as ModeTabType, label: 'Prompty Bazowe', icon: <FileText className="w-4 h-4" /> },
        { id: 'enhance' as ModeTabType, label: 'Enhance Toggles', icon: <WandSparkles className="w-4 h-4" /> },
        { id: 'staging' as ModeTabType, label: 'Staging Personas', icon: <Sofa className="w-4 h-4" /> },
        { id: 'renovation' as ModeTabType, label: 'Renovation Styles', icon: <Hammer className="w-4 h-4" /> },
        { id: 'styles' as ModeTabType, label: 'Interior Styles', icon: <Palette className="w-4 h-4" /> },
    ];

    return (
        <div className="space-y-6">
            {/* Active Generators Header */}
            <ActiveGeneratorsHeader />

            {/* Mode Tabs */}
            <div className="flex gap-2 border-b border-border pb-3 overflow-x-auto">
                {modeTabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveMode(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeMode === tab.id
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:bg-muted/50'
                            }`}
                    >
                        {tab.icon}
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content */}
            {activeMode === 'base-prompts' && <BasePromptsManager />}
            {activeMode === 'enhance' && <EnhanceTogglesManager />}
            {activeMode === 'staging' && <ModeOptionsManager mode="staging" title="Grupy Docelowe (Staging Personas)" />}
            {activeMode === 'renovation' && <ModeOptionsManager mode="renovation" title="Style Renowacji" />}
            {activeMode === 'styles' && <InteriorStylesManager />}
        </div>
    );
}

// ============================================
// Active Generators Header
// ============================================
function ActiveGeneratorsHeader() {
    const [registry, setRegistry] = useState<Record<string, GeneratorRegistryEntry>>({});
    const [active, setActive] = useState<ActiveGeneratorsConfig>({ variant_1: 'gemini', variant_2: 'nanobanana2' });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => { loadConfig(); }, []);

    const loadConfig = async () => {
        try {
            setLoading(true);
            const data = await fetchGeneratorConfig();
            setRegistry(data.registry);
            setActive(data.active);
            setDirty(false);
        } catch {
            toast.error('Nie udalo sie zaladowac konfiguracji generatorow');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await updateActiveGenerators(active);
            toast.success('Zapisano konfiguracje generatorow');
            setDirty(false);
        } catch {
            toast.error('Nie udalo sie zapisac');
        } finally {
            setSaving(false);
        }
    };

    const generatorKeys = Object.keys(registry);

    const getColorClass = (key: string) => {
        const color = registry[key]?.color;
        if (color === 'blue') return 'bg-blue-500/20 text-blue-500 border-blue-500/30';
        if (color === 'purple') return 'bg-purple-500/20 text-purple-500 border-purple-500/30';
        if (color === 'green') return 'bg-green-500/20 text-green-500 border-green-500/30';
        return 'bg-gray-500/20 text-gray-500 border-gray-500/30';
    };

    if (loading) {
        return (
            <div className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Ladowanie konfiguracji generatorow...
                </div>
            </div>
        );
    }

    return (
        <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Cpu className="w-5 h-5 text-muted-foreground" />
                    <div>
                        <h3 className="text-sm font-semibold">Aktywne Generatory</h3>
                        <p className="text-xs text-muted-foreground">Wybierz 2 generatory AI dla wariantow generacji</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Wariant 1</label>
                            <select
                                value={active.variant_1}
                                onChange={e => { setActive({ ...active, variant_1: e.target.value }); setDirty(true); }}
                                className={`px-3 py-1.5 text-sm border rounded-lg bg-background ${getColorClass(active.variant_1)}`}
                            >
                                {generatorKeys.map(key => (
                                    <option key={key} value={key}>{registry[key].label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Wariant 2</label>
                            <select
                                value={active.variant_2}
                                onChange={e => { setActive({ ...active, variant_2: e.target.value }); setDirty(true); }}
                                className={`px-3 py-1.5 text-sm border rounded-lg bg-background ${getColorClass(active.variant_2)}`}
                            >
                                {generatorKeys.map(key => (
                                    <option key={key} value={key}>{registry[key].label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {dirty && (
                        <Button size="sm" onClick={handleSave} disabled={saving}>
                            {saving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                            Zapisz
                        </Button>
                    )}
                </div>
            </div>
            {dirty && (
                <p className="text-xs text-amber-500 mt-2">
                    Niezapisane zmiany. Zmiany dotycza nowych generacji (cache do 5 min).
                </p>
            )}
        </div>
    );
}

// ============================================
// Base Prompts Manager (mode-first with generator toggle)
// ============================================

interface PromptGroup {
    mode: string;
    optionKey: string;
    label: string;
    prompts: Record<string, PromptTemplate>; // keyed by generator
}

function BasePromptsManager() {
    const [allPrompts, setAllPrompts] = useState<PromptTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedGroup, setSelectedGroup] = useState<PromptGroup | null>(null);
    const [activeGenerator, setActiveGenerator] = useState<string>('gemini');
    const [editingContent, setEditingContent] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => { loadPrompts(); }, []);

    const loadPrompts = async () => {
        try {
            setLoading(true);
            const data = await fetchPrompts();
            setAllPrompts(data.prompts);
        } catch {
            toast.error('Nie udalo sie zaladowac promptow');
        } finally {
            setLoading(false);
        }
    };

    // Group prompts by mode + option_key (across generators)
    const groups: PromptGroup[] = (() => {
        const map = new Map<string, PromptGroup>();
        for (const p of allPrompts) {
            const key = `${p.mode}::${p.option_key}`;
            if (!map.has(key)) {
                map.set(key, {
                    mode: p.mode,
                    optionKey: p.option_key,
                    label: p.name.replace(/\s*\(.*?\)\s*$/, ''), // strip generator suffix if any
                    prompts: {},
                });
            }
            map.get(key)!.prompts[p.generator || 'gemini'] = p;
        }
        // Sort by mode then option_key
        const modeOrder = ['enhance', 'staging', 'renovation'];
        return Array.from(map.values()).sort((a, b) => {
            const mi = modeOrder.indexOf(a.mode) - modeOrder.indexOf(b.mode);
            if (mi !== 0) return mi;
            return a.optionKey.localeCompare(b.optionKey);
        });
    })();

    // Group groups by mode for display
    const groupsByMode = (() => {
        const result: Record<string, PromptGroup[]> = {};
        for (const g of groups) {
            if (!result[g.mode]) result[g.mode] = [];
            result[g.mode].push(g);
        }
        return result;
    })();

    const handleSelectGroup = async (group: PromptGroup) => {
        setSelectedGroup(group);
        // Pick the first available generator for this group
        const gen = group.prompts[activeGenerator] ? activeGenerator : Object.keys(group.prompts)[0];
        setActiveGenerator(gen);
        await loadPromptContent(group, gen);
    };

    const loadPromptContent = async (group: PromptGroup, generator: string) => {
        const prompt = group.prompts[generator];
        if (!prompt) {
            setEditingContent('');
            return;
        }
        try {
            const detail = await fetchPromptDetail(prompt.id);
            setEditingContent(detail.template.prompt_template);
        } catch {
            toast.error('Nie udalo sie zaladowac szczegolow');
        }
    };

    const handleGeneratorSwitch = async (gen: string) => {
        setActiveGenerator(gen);
        if (selectedGroup) {
            await loadPromptContent(selectedGroup, gen);
        }
    };

    const handleSave = async () => {
        if (!selectedGroup || !editingContent.trim()) return;
        const prompt = selectedGroup.prompts[activeGenerator];
        try {
            setSaving(true);
            if (prompt) {
                // Update existing prompt
                await updatePrompt(prompt.id, { prompt_template: editingContent });
                toast.success('Zapisano prompt');
            } else {
                // Create new prompt for this generator
                await createPrompt({
                    mode: selectedGroup.mode,
                    option_key: selectedGroup.optionKey,
                    generator: activeGenerator,
                    prompt_template: editingContent,
                });
                toast.success('Utworzono prompt');
            }
            await loadPrompts();
            // Re-select group to refresh prompts map
            const updated = groups.find(g => g.mode === selectedGroup.mode && g.optionKey === selectedGroup.optionKey);
            if (updated) setSelectedGroup(updated);
        } catch {
            toast.error('Nie udalo sie zapisac');
        } finally {
            setSaving(false);
        }
    };

    const currentPrompt = selectedGroup?.prompts[activeGenerator];
    const availableGenerators = Object.keys(GENERATOR_LABELS);

    if (loading) {
        return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin" /></div>;
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-semibold">Prompty Bazowe AI</h3>
                    <p className="text-sm text-muted-foreground">Prompty pogrupowane wg trybu. Przelaczaj generatory w edytorze.</p>
                </div>
                <Button variant="outline" size="sm" onClick={loadPrompts}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Odswiez
                </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Mode-first grouped list */}
                <div className="lg:col-span-1 space-y-4">
                    {Object.entries(groupsByMode).map(([mode, modeGroups]) => (
                        <div key={mode}>
                            <div className="flex items-center gap-2 mb-2">
                                {mode === 'enhance' && <WandSparkles className="w-4 h-4 text-muted-foreground" />}
                                {mode === 'staging' && <Sofa className="w-4 h-4 text-muted-foreground" />}
                                {mode === 'renovation' && <Hammer className="w-4 h-4 text-muted-foreground" />}
                                <span className="text-sm font-semibold text-muted-foreground">
                                    {MODE_LABELS[mode] || mode}
                                </span>
                            </div>
                            <div className="space-y-1 ml-1">
                                {modeGroups.map(group => {
                                    const isSelected = selectedGroup?.mode === group.mode && selectedGroup?.optionKey === group.optionKey;
                                    return (
                                        <button
                                            key={`${group.mode}-${group.optionKey}`}
                                            onClick={() => handleSelectGroup(group)}
                                            className={`w-full text-left px-3 py-2 rounded-lg border transition-colors flex items-center justify-between ${isSelected
                                                ? 'border-primary bg-primary/5'
                                                : 'border-transparent hover:bg-muted/50'
                                                }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <ChevronRight className={`w-3 h-3 transition-transform ${isSelected ? 'rotate-90' : ''}`} />
                                                <span className="text-sm font-medium">{group.optionKey}</span>
                                            </div>
                                            <div className="flex gap-1">
                                                {availableGenerators.map(gen => (
                                                    <span
                                                        key={gen}
                                                        className={`w-2 h-2 rounded-full ${
                                                            group.prompts[gen]
                                                                ? gen === 'gemini' ? 'bg-blue-500' : gen === 'openai' ? 'bg-green-500' : gen.startsWith('flux') ? 'bg-amber-500' : 'bg-purple-500'
                                                                : 'bg-gray-300'
                                                        }`}
                                                        title={`${GENERATOR_LABELS[gen]}: ${group.prompts[gen] ? 'skonfigurowany' : 'brak (fallback)'}`}
                                                    />
                                                ))}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                    {groups.length === 0 && (
                        <div className="text-center py-8 text-muted-foreground text-sm">
                            Brak promptow
                        </div>
                    )}
                </div>

                {/* Editor with generator toggle */}
                <div className="lg:col-span-2">
                    {selectedGroup ? (
                        <div className="bg-card border border-border rounded-xl p-4 space-y-4">
                            {/* Header */}
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="font-semibold">
                                        {MODE_LABELS[selectedGroup.mode] || selectedGroup.mode}
                                        {' '}&rsaquo;{' '}
                                        {selectedGroup.optionKey}
                                    </h4>
                                    {currentPrompt && (
                                        <span className="text-xs text-muted-foreground">v{currentPrompt.version}</span>
                                    )}
                                </div>
                                <Button size="sm" onClick={handleSave} disabled={saving || !editingContent.trim()}>
                                    {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                                    Zapisz
                                </Button>
                            </div>

                            {/* Generator toggle tabs */}
                            <div className="flex gap-1 bg-muted/50 rounded-lg p-1 overflow-x-auto">
                                {availableGenerators.map(gen => {
                                    const hasPrompt = !!selectedGroup.prompts[gen];
                                    const isActive = activeGenerator === gen;
                                    return (
                                        <button
                                            key={gen}
                                            onClick={() => handleGeneratorSwitch(gen)}
                                            className={`shrink-0 px-3 py-1.5 text-sm rounded-md transition-colors flex items-center justify-center gap-2 ${
                                                isActive
                                                    ? 'bg-background shadow-sm font-medium'
                                                    : 'text-muted-foreground hover:text-foreground'
                                            }`}
                                        >
                                            <span className={`w-2 h-2 rounded-full shrink-0 ${
                                                gen === 'gemini' ? 'bg-blue-500' : gen === 'openai' ? 'bg-green-500' : gen.startsWith('flux') ? 'bg-amber-500' : 'bg-purple-500'
                                            }`} />
                                            <span className="whitespace-nowrap">{GENERATOR_LABELS[gen]}</span>
                                            {!hasPrompt && (
                                                <span className="text-xs text-amber-500 whitespace-nowrap">(fallback)</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Textarea */}
                            <textarea
                                value={editingContent}
                                onChange={e => setEditingContent(e.target.value)}
                                className="w-full h-[400px] px-4 py-3 text-sm font-mono border border-border rounded-lg bg-background resize-none"
                                placeholder={currentPrompt ? "Tresc prompta..." : `Wpisz prompt dla ${GENERATOR_LABELS[activeGenerator] || activeGenerator}...`}
                            />
                            <div className="text-xs text-muted-foreground flex items-center justify-between">
                                <span><strong>Zmienne:</strong> {'${style}'}, {'${persona}'}, {'${mode}'} - zostana zastapione dynamicznie</span>
                                {!currentPrompt && editingContent.trim() && (
                                    <span className="text-amber-500">Nowy prompt - kliknij Zapisz aby utworzyc</span>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-muted/30 border border-dashed border-border rounded-xl p-12 text-center text-muted-foreground">
                            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>Wybierz prompt z listy aby edytowac</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ============================================
// Enhance Toggles Manager
// ============================================
function EnhanceTogglesManager() {
    const [toggles, setToggles] = useState<EnhanceToggle[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<Partial<EnhanceToggle>>({});
    const [isAdding, setIsAdding] = useState(false);
    const [newForm, setNewForm] = useState<Partial<EnhanceToggle>>({ default_enabled: false });

    useEffect(() => { loadToggles(); }, []);

    const loadToggles = async () => {
        try {
            setLoading(true);
            const data = await fetchEnhanceToggles();
            setToggles(data.toggles);
        } catch (error) {
            toast.error('Nie udało się załadować toggle\'i');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (id: string) => {
        try {
            await updateEnhanceToggle(id, editForm);
            toast.success('Zapisano zmiany');
            setEditingId(null);
            loadToggles();
        } catch (error) {
            toast.error('Nie udało się zapisać');
        }
    };

    const handleAdd = async () => {
        if (!newForm.toggle_key || !newForm.name || !newForm.prompt_fragment) {
            toast.error('Wypełnij wszystkie wymagane pola');
            return;
        }
        try {
            await createEnhanceToggle(newForm);
            toast.success('Dodano nowy toggle');
            setIsAdding(false);
            setNewForm({ default_enabled: false });
            loadToggles();
        } catch (error) {
            toast.error('Nie udało się dodać');
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Czy na pewno chcesz usunąć ten toggle?')) return;
        try {
            await deleteEnhanceToggle(id);
            toast.success('Usunięto toggle');
            loadToggles();
        } catch (error) {
            toast.error('Nie udało się usunąć');
        }
    };

    if (loading) {
        return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin" /></div>;
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-semibold">Toggle'e Poprawy Jakości</h3>
                    <p className="text-sm text-muted-foreground">Opcje dodające fragmenty do promptu enhance mode</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={loadToggles}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Odśwież
                    </Button>
                    <Button size="sm" onClick={() => setIsAdding(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Dodaj
                    </Button>
                </div>
            </div>

            {/* Add form */}
            {isAdding && (
                <div className="bg-muted/30 border border-border rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-muted-foreground">Klucz (toggle_key)*</label>
                            <input
                                type="text"
                                value={newForm.toggle_key || ''}
                                onChange={e => setNewForm({ ...newForm, toggle_key: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                                placeholder="np. newFeature"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">Nazwa*</label>
                            <input
                                type="text"
                                value={newForm.name || ''}
                                onChange={e => setNewForm({ ...newForm, name: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                                placeholder="np. Nowa Funkcja"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">Opis</label>
                        <input
                            type="text"
                            value={newForm.description || ''}
                            onChange={e => setNewForm({ ...newForm, description: e.target.value })}
                            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                            placeholder="Opis dla użytkownika"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">Fragment promptu*</label>
                        <textarea
                            value={newForm.prompt_fragment || ''}
                            onChange={e => setNewForm({ ...newForm, prompt_fragment: e.target.value })}
                            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background font-mono resize-none h-20"
                            placeholder="Tekst dodawany do promptu..."
                        />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">URL obrazka ilustrującego</label>
                        <input
                            type="text"
                            value={newForm.illustration_url || ''}
                            onChange={e => setNewForm({ ...newForm, illustration_url: e.target.value })}
                            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                            placeholder="https://..."
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={newForm.default_enabled || false}
                            onChange={e => setNewForm({ ...newForm, default_enabled: e.target.checked })}
                            id="new-default"
                        />
                        <label htmlFor="new-default" className="text-sm">Domyślnie włączony</label>
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" onClick={handleAdd}>
                            <Save className="w-4 h-4 mr-1" />
                            Zapisz
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setIsAdding(false); setNewForm({ default_enabled: false }); }}>
                            <X className="w-4 h-4 mr-1" />
                            Anuluj
                        </Button>
                    </div>
                </div>
            )}

            {/* Toggles list */}
            <div className="space-y-3">
                {toggles.map(toggle => (
                    <div key={toggle.id} className="bg-card border border-border rounded-lg p-4">
                        {editingId === toggle.id ? (
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs text-muted-foreground">Nazwa</label>
                                        <input
                                            type="text"
                                            value={editForm.name ?? toggle.name}
                                            onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                                            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-muted-foreground">Opis</label>
                                        <input
                                            type="text"
                                            value={editForm.description ?? toggle.description ?? ''}
                                            onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                                            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs text-muted-foreground">Fragment promptu</label>
                                    <textarea
                                        value={editForm.prompt_fragment ?? toggle.prompt_fragment}
                                        onChange={e => setEditForm({ ...editForm, prompt_fragment: e.target.value })}
                                        className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background font-mono resize-none h-20"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-muted-foreground">URL obrazka</label>
                                    <input
                                        type="text"
                                        value={editForm.illustration_url ?? toggle.illustration_url ?? ''}
                                        onChange={e => setEditForm({ ...editForm, illustration_url: e.target.value })}
                                        className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <Button size="sm" onClick={() => handleSave(toggle.id)}>
                                        <Save className="w-4 h-4 mr-1" />
                                        Zapisz
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => { setEditingId(null); setEditForm({}); }}>
                                        <X className="w-4 h-4 mr-1" />
                                        Anuluj
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-start gap-4">
                                {toggle.illustration_url ? (
                                    <img src={toggle.illustration_url} alt={toggle.name} className="w-16 h-16 rounded-lg object-cover" />
                                ) : (
                                    <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                                        <Image className="w-6 h-6 text-muted-foreground" />
                                    </div>
                                )}
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium">{toggle.name}</span>
                                        <span className="text-xs px-2 py-0.5 bg-muted rounded">{toggle.toggle_key}</span>
                                        {toggle.default_enabled && (
                                            <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-500 rounded">Domyślnie wł.</span>
                                        )}
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-1">{toggle.description}</p>
                                    <p className="text-xs font-mono text-muted-foreground mt-2 line-clamp-2">{toggle.prompt_fragment}</p>
                                </div>
                                <div className="flex gap-1">
                                    <Button variant="ghost" size="icon" onClick={() => { setEditingId(toggle.id); setEditForm({}); }}>
                                        <Edit2 className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" onClick={() => handleDelete(toggle.id)}>
                                        <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

// ============================================
// Mode Options Manager (Staging/Renovation)
// ============================================
function ModeOptionsManager({ mode, title }: { mode: 'staging' | 'renovation'; title: string }) {
    const [options, setOptions] = useState<ModeOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<Partial<ModeOption>>({});
    const [isAdding, setIsAdding] = useState(false);
    const [newForm, setNewForm] = useState<Partial<ModeOption>>({ mode });

    useEffect(() => { loadOptions(); }, [mode]);

    const loadOptions = async () => {
        try {
            setLoading(true);
            const data = await fetchModeOptions(mode);
            setOptions(data.options);
        } catch (error) {
            toast.error('Nie udało się załadować opcji');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (id: string) => {
        try {
            await updateModeOption(id, editForm);
            toast.success('Zapisano zmiany');
            setEditingId(null);
            loadOptions();
        } catch (error) {
            toast.error('Nie udało się zapisać');
        }
    };

    const handleAdd = async () => {
        if (!newForm.option_key || !newForm.name) {
            toast.error('Wypełnij wszystkie wymagane pola');
            return;
        }
        try {
            await createModeOption({ ...newForm, mode });
            toast.success('Dodano nową opcję');
            setIsAdding(false);
            setNewForm({ mode });
            loadOptions();
        } catch (error) {
            toast.error('Nie udało się dodać');
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Czy na pewno chcesz usunąć tę opcję?')) return;
        try {
            await deleteModeOption(id);
            toast.success('Usunięto opcję');
            loadOptions();
        } catch (error) {
            toast.error('Nie udało się usunąć');
        }
    };

    if (loading) {
        return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin" /></div>;
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-semibold">{title}</h3>
                    <p className="text-sm text-muted-foreground">
                        {mode === 'staging' ? 'Grupy docelowe dla wirtualnego umeblowania' : 'Style dostępne w trybie renowacji'}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={loadOptions}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Odśwież
                    </Button>
                    <Button size="sm" onClick={() => setIsAdding(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Dodaj
                    </Button>
                </div>
            </div>

            {/* Add form */}
            {isAdding && (
                <div className="bg-muted/30 border border-border rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="text-xs text-muted-foreground">Klucz (option_key)*</label>
                            <input
                                type="text"
                                value={newForm.option_key || ''}
                                onChange={e => setNewForm({ ...newForm, option_key: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                                placeholder="np. luxury"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">Nazwa*</label>
                            <input
                                type="text"
                                value={newForm.name || ''}
                                onChange={e => setNewForm({ ...newForm, name: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                                placeholder="np. Luksusowy"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">Ikona (Lucide)</label>
                            <input
                                type="text"
                                value={newForm.icon || ''}
                                onChange={e => setNewForm({ ...newForm, icon: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                                placeholder="np. Diamond"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">Opis</label>
                        <input
                            type="text"
                            value={newForm.description || ''}
                            onChange={e => setNewForm({ ...newForm, description: e.target.value })}
                            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                            placeholder="Opis dla użytkownika"
                        />
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" onClick={handleAdd}>
                            <Save className="w-4 h-4 mr-1" />
                            Zapisz
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setIsAdding(false); setNewForm({ mode }); }}>
                            <X className="w-4 h-4 mr-1" />
                            Anuluj
                        </Button>
                    </div>
                </div>
            )}

            {/* Options list */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {options.map(option => (
                    <div key={option.id} className="bg-card border border-border rounded-lg p-4">
                        {editingId === option.id ? (
                            <div className="space-y-3">
                                <input
                                    type="text"
                                    value={editForm.name ?? option.name}
                                    onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                                    placeholder="Nazwa"
                                />
                                <input
                                    type="text"
                                    value={editForm.icon ?? option.icon ?? ''}
                                    onChange={e => setEditForm({ ...editForm, icon: e.target.value })}
                                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                                    placeholder="Ikona (Lucide)"
                                />
                                <input
                                    type="text"
                                    value={editForm.description ?? option.description ?? ''}
                                    onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                                    placeholder="Opis"
                                />
                                <div className="flex gap-2">
                                    <Button size="sm" onClick={() => handleSave(option.id)}>
                                        <Save className="w-4 h-4" />
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => { setEditingId(null); setEditForm({}); }}>
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium">{option.name}</span>
                                        <span className="text-xs text-muted-foreground">{option.icon && `(${option.icon})`}</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-1">{option.description}</p>
                                    <span className="text-xs px-2 py-0.5 bg-muted rounded mt-2 inline-block">{option.option_key}</span>
                                </div>
                                <div className="flex gap-1">
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingId(option.id); setEditForm({}); }}>
                                        <Edit2 className="w-3 h-3" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(option.id)}>
                                        <Trash2 className="w-3 h-3 text-red-500" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

// ============================================
// Interior Styles Manager
// ============================================
function InteriorStylesManager() {
    const [styles, setStyles] = useState<InteriorStyle[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<Partial<InteriorStyle>>({});
    const [isAdding, setIsAdding] = useState(false);
    const [newForm, setNewForm] = useState<Partial<InteriorStyle>>({});

    useEffect(() => { loadStyles(); }, []);

    const loadStyles = async () => {
        try {
            setLoading(true);
            const data = await fetchInteriorStyles();
            setStyles(data.styles);
        } catch (error) {
            toast.error('Nie udało się załadować stylów');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (id: string) => {
        try {
            await updateInteriorStyle(id, editForm);
            toast.success('Zapisano zmiany');
            setEditingId(null);
            loadStyles();
        } catch (error) {
            toast.error('Nie udało się zapisać');
        }
    };

    const handleAdd = async () => {
        if (!newForm.style_key || !newForm.name || !newForm.prompt_value) {
            toast.error('Wypełnij wszystkie wymagane pola');
            return;
        }
        try {
            await createInteriorStyle(newForm);
            toast.success('Dodano nowy styl');
            setIsAdding(false);
            setNewForm({});
            loadStyles();
        } catch (error) {
            toast.error('Nie udało się dodać');
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Czy na pewno chcesz usunąć ten styl?')) return;
        try {
            await deleteInteriorStyle(id);
            toast.success('Usunięto styl');
            loadStyles();
        } catch (error) {
            toast.error('Nie udało się usunąć');
        }
    };

    if (loading) {
        return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin" /></div>;
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-semibold">Style Wnętrz</h3>
                    <p className="text-sm text-muted-foreground">Style używane jako {"${style}"} w promptach staging</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={loadStyles}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Odśwież
                    </Button>
                    <Button size="sm" onClick={() => setIsAdding(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Dodaj
                    </Button>
                </div>
            </div>

            {/* Add form */}
            {isAdding && (
                <div className="bg-muted/30 border border-border rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="text-xs text-muted-foreground">Klucz (style_key)*</label>
                            <input
                                type="text"
                                value={newForm.style_key || ''}
                                onChange={e => setNewForm({ ...newForm, style_key: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                                placeholder="np. japandi"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">Nazwa*</label>
                            <input
                                type="text"
                                value={newForm.name || ''}
                                onChange={e => setNewForm({ ...newForm, name: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                                placeholder="np. Japandi"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">Wartość promptu*</label>
                            <input
                                type="text"
                                value={newForm.prompt_value || ''}
                                onChange={e => setNewForm({ ...newForm, prompt_value: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                                placeholder="np. japanese scandinavian fusion"
                            />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" onClick={handleAdd}>
                            <Save className="w-4 h-4 mr-1" />
                            Zapisz
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setIsAdding(false); setNewForm({}); }}>
                            <X className="w-4 h-4 mr-1" />
                            Anuluj
                        </Button>
                    </div>
                </div>
            )}

            {/* Styles list */}
            <div className="bg-card border border-border rounded-xl overflow-hidden">
                <table className="w-full">
                    <thead className="bg-muted/50">
                        <tr>
                            <th className="text-left p-3 text-sm font-medium">Klucz</th>
                            <th className="text-left p-3 text-sm font-medium">Nazwa</th>
                            <th className="text-left p-3 text-sm font-medium">Wartość promptu</th>
                            <th className="text-right p-3 text-sm font-medium">Akcje</th>
                        </tr>
                    </thead>
                    <tbody>
                        {styles.map(style => (
                            <tr key={style.id} className="border-t border-border">
                                {editingId === style.id ? (
                                    <>
                                        <td className="p-3">
                                            <span className="text-sm font-mono">{style.style_key}</span>
                                        </td>
                                        <td className="p-3">
                                            <input
                                                type="text"
                                                value={editForm.name ?? style.name}
                                                onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                                                className="w-full px-2 py-1 text-sm border border-border rounded bg-background"
                                            />
                                        </td>
                                        <td className="p-3">
                                            <input
                                                type="text"
                                                value={editForm.prompt_value ?? style.prompt_value}
                                                onChange={e => setEditForm({ ...editForm, prompt_value: e.target.value })}
                                                className="w-full px-2 py-1 text-sm border border-border rounded bg-background font-mono"
                                            />
                                        </td>
                                        <td className="p-3 text-right">
                                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleSave(style.id)}>
                                                <Save className="w-4 h-4" />
                                            </Button>
                                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingId(null); setEditForm({}); }}>
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td className="p-3 text-sm font-mono">{style.style_key}</td>
                                        <td className="p-3 text-sm font-medium">{style.name}</td>
                                        <td className="p-3 text-sm font-mono text-muted-foreground">{style.prompt_value}</td>
                                        <td className="p-3 text-right">
                                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingId(style.id); setEditForm({}); }}>
                                                <Edit2 className="w-4 h-4" />
                                            </Button>
                                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(style.id)}>
                                                <Trash2 className="w-4 h-4 text-red-500" />
                                            </Button>
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default AdvancedPromptsTab;
